import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

export function useVersionCheck() {
  const initialVersion = useRef<string | null>(null);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  // The version currently advertised by the server (whether or not it changed).
  // Exposed so consumers can scope per-version UI state (e.g. dismissing a dot).
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch(`/build-version.json?t=${Date.now()}`);
      if (!res.ok) return;
      const data = await res.json();
      const version = data.version;
      if (!version) return;

      setLatestVersion(version);

      if (initialVersion.current === null) {
        initialVersion.current = version;
      } else if (version !== initialVersion.current) {
        setNewVersionAvailable(true);
      }
    } catch {
      // silently ignore — offline, dev server, etc.
    }
  }, []);

  useEffect(() => {
    checkVersion();

    const interval = setInterval(checkVersion, POLL_INTERVAL);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [checkVersion]);

  return { newVersionAvailable, latestVersion };
}
